import React, { FC, useId, useRef, useState } from 'react';
import { LinkProps } from 'react-router-dom';

import { Label } from '../../../generated/graphql';
import translate from '../../../i18n/translate';
import LabelUpdateForm from '../../Forms/LabelUpdateForm';
import LabelCreate from '../../MUI/Compose/LabelCreate';
import { ExternalPopoverProps } from '../../MUI/Popover';
import MetaContainerInline, {
  Props as PropsInline,
} from '../../MetaContainerInline';
import { Props as PropsPopup } from '../../MetaContainerPopup';
import { getFormattedHexColor } from '../helpers';

type Props = {
  className?: string;
  dataColor: Label['color'];
  dataText?: Label['text'];
  dataTextSelected?: Label['text'];
  isActive: boolean;
  labelClose?: string;
  labelsExisting: Label['text'][];
  linkToggleProps?: LinkProps | null;
  requestModeView?: () => void;
  requestSave: (text: Label['text'], color: Label['color']) => void;
  textHeading?: string;
};

/**
 * Label create/edit form
 *
 * @param props                  Props passed to the component
 * @param props.className        styled-components generated class name, needed for styling
 * @param props.dataColor        Label color
 * @param props.dataTextSelected The currently selected label/text to edit
 * @param props.isActive         Whether the component should be visible
 * @param props.labelClose       The text to use as the close button label
 * @param props.labelsExisting   Labels already present
 * @param props.linkToggleProps  Props for the toggle link
 * @param props.requestModeView  Request to switch to view mode, showing a list of labels
 * @param props.requestSave      Callback to invoke to request a label to be updated
 * @param props.textHeading      The text to show for heading
 * @returns                      The component itself
 */
const MetaLabelEdit: FC<Props> = ({
  className,
  dataColor,
  dataTextSelected = '',
  isActive,
  labelClose = translate('GENERAL__CLOSE'),
  labelsExisting,
  linkToggleProps,
  requestModeView,
  requestSave,
  textHeading = translate('LABELS__EDIT__HEADING'),
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [color, setColor] = useState(dataColor);
  const [text, setText] = useState(dataTextSelected);

  const initialColor = useRef(dataColor);
  const initialText = useRef(dataTextSelected);
  const [isClearDisabled, setIsClearDisabled] = useState(true);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  const idForm = useId();
  const idHeading = useId();

  const labelConfirm = translate('GENERAL__SAVE');

  /**
   * Clear the form and disable save button
   */
  const handleClear = () => {
    setColor(dataColor);
    setText(dataTextSelected);
    setIsSaveDisabled(true);
    setIsClearDisabled(true);
  };

  /**
   * Set color value and check if save button should be disabled
   *
   * @param value New value of color input
   */
  const requestChangeColor = (value: string): void => {
    setColor(value);
    setIsSaveDisabled(
      initialText.current === text && initialColor.current === value,
    );
    setIsClearDisabled(value === '' || initialColor.current === value);
  };

  /**
   * Set text value and check if save button should be disabled
   *
   * @param value New value of text input
   */
  const requestChangeText = (value: string): void => {
    setText(value);
    setIsSaveDisabled(
      initialText.current === value && initialColor.current === color,
    );
    setIsClearDisabled(value === '' || initialText.current === value);
  };

  /**
   * Request to save the label
   */
  const requestSaveLabel = () => {
    requestSave(text, getFormattedHexColor(color));
    setAnchorEl(null);
  };

  // Content is the same for both inline and popup meta
  const children = (
    <LabelUpdateForm
      ariaDescribedBy={idHeading}
      ariaLabelledBy={idHeading}
      dataColor={color}
      dataText={text}
      idForm={idForm}
      labelsExisting={labelsExisting}
      requestChangeColor={requestChangeColor}
      requestChangeText={requestChangeText}
      requestSave={requestSaveLabel}
    />
  );

  // Props used for both inline and popup meta
  const propsCommon: PropsInline | PropsPopup = {
    className,
    idForm,
    idHeading,
    isSaveDisabled,
    labelClose,
    labelConfirm,
    onClose: requestModeView,
    textHeading,
  };

  const popoverHandlers: ExternalPopoverProps = {
    anchorEl,
    onStateChange: setAnchorEl,
  };

  // If linkToggleProps are passed, this means that we want to render as a popup
  // If it's not (null is default), that means we want to show inline (and not mess with URL)
  if (linkToggleProps === null) {
    return (
      <MetaContainerInline {...propsCommon}>{children}</MetaContainerInline>
    );
  }

  return (
    <LabelCreate
      externalHandlerProps={popoverHandlers}
      formId={idForm}
      isActive={isActive}
      isClearDisabled={isClearDisabled}
      onClose={requestModeView}
      requestClear={handleClear}
    >
      {children}
    </LabelCreate>
  );
};

export default MetaLabelEdit;
